import { useRef, useState } from 'react';
import { Form, Button, FloatingLabel } from 'react-bootstrap';
import { changePassword } from '../services/PasswordResetService';
import { requireAlphaNumeric } from '../utilities/FormUtility';
import { FormFeedback } from './FormFeedback';
import { KeyFill } from 'react-bootstrap-icons';
import { AccountPageHeader } from './AccountPageHeader';
import {
    validatePassword,
    validateConfirmPassword,
    validationClass,
} from '../utilities/validators';

function ChangePasswordForm() {
    const oldPasswordRef = useRef(null);

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [newPasswordResult, setNewPasswordResult] = useState(null);
    const [confirmPasswordResult, setConfirmPasswordResult] = useState(null);

    const [loading, setLoading] = useState(false);
    const [feedbackType, setFeedbackType] = useState('error');
    const [feedbackMessage, setFeedbackMessage] = useState(null);

    async function attemptChange(event) {
        event.preventDefault();
        event.stopPropagation();

        const allValid = newPasswordResult?.valid === true && confirmPasswordResult?.valid === true;

        if (!allValid) {
            setFeedbackType('error');
            setFeedbackMessage("Please enter valid information.");
            return;
        }

        setLoading(true);
        setFeedbackMessage(null);

        try {
            await changePassword(oldPasswordRef.current.value, newPassword);
            setFeedbackType('success');
            setFeedbackMessage("Your password has been changed successfully.");
            oldPasswordRef.current.value = "";
            setNewPassword("");
            setConfirmPassword("");
            setNewPasswordResult(null);
            setConfirmPasswordResult(null);
        } catch (err) {
            setFeedbackType('error');
            if (err.message === 'password') {
                setFeedbackMessage("Incorrect current password.");
            } else {
                setFeedbackMessage("There was an error changing your password. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    }

    function handleNewPasswordChange(e) {
        const value = e.target.value;
        setNewPassword(value);
        setNewPasswordResult(validatePassword(value));
        setConfirmPasswordResult(validateConfirmPassword(confirmPassword, value));
    }

    function handleConfirmPasswordChange(e) {
        const value = e.target.value;
        setConfirmPassword(value);
        setConfirmPasswordResult(validateConfirmPassword(value, newPassword));
    }

    return (
        <div className="register-page">
            <AccountPageHeader
                icon={<KeyFill />}
                title="Change Password"
                description="Update your account password"
            />
            <div className="register-card">

                <Form noValidate onSubmit={attemptChange}>
                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="opassword" label="Current Password" className="mb-1">
                            <Form.Control
                                type="password"
                                placeholder="Current Password"
                                ref={oldPasswordRef}
                            />
                        </FloatingLabel>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="rpassword" label="New Password" className="mb-1">
                            <Form.Control
                                type="password"
                                placeholder="New Password"
                                value={newPassword}
                                onChange={handleNewPasswordChange}
                                onKeyPress={requireAlphaNumeric}
                                className={validationClass(newPasswordResult)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {newPasswordResult?.message}
                            </Form.Control.Feedback>
                        </FloatingLabel>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <FloatingLabel controlId="crpassword" label="Confirm New Password" className="mb-1">
                            <Form.Control
                                type="password"
                                placeholder="Confirm New Password"
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                                onKeyPress={requireAlphaNumeric}
                                className={validationClass(confirmPasswordResult)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {confirmPasswordResult?.message}
                            </Form.Control.Feedback>
                        </FloatingLabel>
                    </Form.Group>

                    <FormFeedback type="loading" message={loading ? "Changing password…" : null} />
                    <FormFeedback type={feedbackType} message={feedbackMessage} />

                    <Button className="btn-register" type="submit" disabled={loading}>
                        {loading ? "Changing Password…" : "Change Password"}
                    </Button>
                </Form>
            </div>
        </div>
    );
}

export { ChangePasswordForm };
